import {skipToken} from '@reduxjs/toolkit/query';
import {DataStatus, openDialog} from 'platform/components';
import {Box, Show, Spinner, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {isNil, isNotNil} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {
  TestDriveCalendarDrive,
  TestDriveCalendarVehicle,
  useSearchTestDriveCalendarQuery,
} from '@dms/api/sales';
import {
  openCreateTestDriveDialog,
  TEST_DRIVE_DIALOG_ID,
  TestDriveDialog,
} from '@dms/features/test-drive';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {usePermissions} from '@dms/shared';

import {TestDriveCalendarToolbar} from './components/TestDriveCalendarToolbar';
import {TestDriveResourceCalendar} from './components/TestDriveResourceCalendar';
import {useTestDriveCalendarControl} from './hooks/useTestDriveCalendarControl';
import {TestDriveCalendarSlot} from './utils/getSlotFromPointer';
import {openTestDriveCalendarSettingsDialog} from './utils/openTestDriveCalendarSettingsDialog';

type CalendarState =
  | 'noAssignedBranch'
  | 'loading'
  | 'error'
  | 'refetching'
  | 'noSearchResult'
  | 'noVehicle'
  | 'ready';

export function TestDriveCalendar() {
  const control = useTestDriveCalendarControl();
  const {data, isLoading, isFetching, isError, refetch} = useSearchTestDriveCalendarQuery(
    control.searchArg ?? skipToken
  );
  const [
    hasEditTestDrivePermission,
    hasCreateTestDrivePermission,
    hasCreateBusinessCasePermission,
  ] = usePermissions({
    permissionKeys: ['editTestDrive', 'createTestDrive', 'createBusinessCaseSelling'],
  });

  /* Creating a drive here may have to create the business case it hangs on, so both are required. */
  const canCreateTestDrive = hasCreateTestDrivePermission && hasCreateBusinessCasePermission;

  const handleOpenCreateTestDriveDialog = (
    vehicle?: TestDriveCalendarVehicle,
    slot?: TestDriveCalendarSlot
  ) =>
    openCreateTestDriveDialog({
      vehicleId: vehicle?.vehicleId,
      branchId: vehicle?.branchId,
      defaultSlot: slot,
      isVehicleChangeHidden: isNotNil(vehicle),
      isCalendarVehiclePicker: isNil(vehicle),
      isDeleteHidden: true,
    });

  const openTestDriveDialog = (drive: TestDriveCalendarDrive) => {
    if (isNil(drive.businessCaseId)) {
      return;
    }

    openDialog(
      <TestDriveDialog
        testDriveId={drive.testDriveId}
        businessCaseId={drive.businessCaseId}
        isDeleteHidden
        isVehicleChangeHidden
        isReadOnly={!hasEditTestDrivePermission}
      />,
      {
        id: TEST_DRIVE_DIALOG_ID,
        title: i18n.t('entity.testDrive.labels.editTestDrive'),
        size: 'large',
        withAdditionalFooter: true,
      }
    );
  };

  const openSettingsDialog = () =>
    openTestDriveCalendarSettingsDialog({
      assignedBranches: control.assignedBranches,
      'data-testid': testIds.testDrive.overviewCalendar('settingsDialog'),
    });

  const vehicles = data?.vehicles ?? [];

  const state: CalendarState = match({
    hasAssignedBranch: control.hasAssignedBranch,
    isLoading,
    isFetching,
    isError,
    isEmpty: isNilOrEmpty(vehicles),
    hasSearch: isNotNilOrEmpty(control.searchArg?.search),
  })
    .with({hasAssignedBranch: false}, () => 'noAssignedBranch' as const)
    .with({isLoading: true}, () => 'loading' as const)
    .with({isError: true}, () => 'error' as const)
    .with({isFetching: true, isEmpty: true}, () => 'refetching' as const)
    .with({isEmpty: true, hasSearch: true}, () => 'noSearchResult' as const)
    .with({isEmpty: true}, () => 'noVehicle' as const)
    .otherwise(() => 'ready' as const);

  return (
    <Box padding={4} data-testid={testIds.testDrive.overviewCalendar()}>
      <VStack spacing={4}>
        {match(state)
          .with('noAssignedBranch', () => (
            <DataStatus
              isEmpty
              emptyMessage={i18n.t('page.testDrive.labels.noAssignedBranch')}
              data-testid={testIds.testDrive.overviewCalendar('noAssignedBranch')}
            />
          ))
          .with('loading', () => (
            <DataStatus isLoading data-testid={testIds.testDrive.overviewCalendar('loading')} />
          ))
          .with('error', () => (
            <DataStatus
              isError
              errorAction={{
                title: i18n.t('general.actions.tryAgain'),
                onClick: () => void refetch(),
              }}
              data-testid={testIds.testDrive.overviewCalendar('error')}
            />
          ))
          .otherwise(() => (
            <>
              <TestDriveCalendarToolbar
                rangeMode={control.rangeMode}
                selectedDate={control.selectedDate}
                assignedBranches={control.assignedBranches}
                search={control.search}
                page={control.page}
                size={control.size}
                totalCount={data?.totalCount ?? 0}
                hasCreateTestDrivePermission={canCreateTestDrive}
                onCreateTestDrive={() => handleOpenCreateTestDriveDialog()}
                onRangeModeChange={control.setRangeMode}
                onSelectedDateChange={control.setSelectedDate}
                onSearchChange={control.setSearch}
                onPageChange={control.setPage}
                onSizeChange={control.setSize}
                data-testid={testIds.testDrive.overviewCalendar('toolbar')}
              />
              {match(state)
                .with('refetching', () => (
                  <DataStatus
                    isLoading
                    data-testid={testIds.testDrive.overviewCalendar('refetching')}
                  />
                ))
                .with('noSearchResult', () => (
                  <DataStatus
                    isEmpty
                    emptyMessage={i18n.t('entity.vehicle.labels.noMatchingVehicles')}
                    action={{
                      title: i18n.t('general.actions.clear'),
                      onClick: () => control.setSearch(null),
                    }}
                    data-testid={testIds.testDrive.overviewCalendar('noSearchResult')}
                  />
                ))
                .with('noVehicle', () => (
                  <DataStatus
                    isEmpty
                    emptyMessage={i18n.t('page.testDrive.labels.noVehicleMatchesSettings')}
                    action={{title: i18n.t('general.labels.settings'), onClick: openSettingsDialog}}
                    data-testid={testIds.testDrive.overviewCalendar('noVehicle')}
                  />
                ))
                .otherwise(() => (
                  <Box position="relative">
                    <Box position="relative" zIndex="base">
                      <TestDriveResourceCalendar
                        vehicles={vehicles}
                        windowStart={control.windowStart}
                        rangeDays={control.rangeMode}
                        onTestDriveClick={openTestDriveDialog}
                        onSlotClick={
                          canCreateTestDrive ? handleOpenCreateTestDriveDialog : undefined
                        }
                        data-testid={testIds.testDrive.overviewCalendar('grid')}
                      />
                    </Box>
                    <Show when={isFetching}>
                      <Spinner
                        variant="overlay"
                        data-testid={testIds.testDrive.overviewCalendar('refetching')}
                      />
                    </Show>
                  </Box>
                ))}
            </>
          ))}
      </VStack>
    </Box>
  );
}
